import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import { Navigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import AuthenticationRoutes from './AuthenticationRoutes';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard')));

// pages routing
const Invoices = Loadable(lazy(() => import('views/pages/Invoices')));
const PaymentReceipts = Loadable(
  lazy(() => import('views/pages/PaymentReceipts'))
);
const Expenses = Loadable(lazy(() => import('views/pages/Expenses')));
const FixedAssets = Loadable(lazy(() => import('views/pages/FixedAssets')));
const Investment = Loadable(lazy(() => import('views/pages/Investment')));

// library
const ClientList = Loadable(
  lazy(() => import('views/pages/Library/ClientList'))
);
const ProductList = Loadable(
  lazy(() => import('views/pages/Library/ProductList'))
);
const VendorList = Loadable(
  lazy(() => import('views/pages/Library/VendorList'))
);
const ExpenseHeads = Loadable(
  lazy(() => import('views/pages/Library/ExpenseHeads'))
);
const ExpenseSubHeads = Loadable(
  lazy(() => import('views/pages/Library/ExpenseSubHeads'))
);
const AssetTitles = Loadable(
  lazy(() => import('views/pages/Library/AssetTitles'))
);

// report
const ReportSummary = Loadable(
  lazy(() => import('views/pages/Report/ReportSummary'))
);
const DailyReport = Loadable(
  lazy(() => import('views/pages/Report/DailyReport'))
);
const DueReport = Loadable(lazy(() => import('views/pages/Report/DueReport')));
const AdvanceReport = Loadable(
  lazy(() => import('views/pages/Report/AdvanceReport'))
);
const ExpenseSummary = Loadable(
  lazy(() => import('views/pages/Report/ExpenseSummary'))
);
// end pages

// vehicle
const FuelExpenses = Loadable(
  lazy(() => import('views/vehicle/OperationExpenses/FuelExpenses'))
);
const DriverSalaries = Loadable(
  lazy(() => import('views/vehicle/OperationExpenses/DriverSalaries'))
);
const OtherExpenses = Loadable(
  lazy(() => import('views/vehicle/OperationExpenses/OtherExpenses'))
);
const RoutineServices = Loadable(
  lazy(() => import('views/vehicle/MaintenanceExpenses/RoutineServices'))
);
const MasterServices = Loadable(
  lazy(() => import('views/vehicle/MaintenanceExpenses/MasterServices'))
);
const OtherMaintenances = Loadable(
  lazy(() => import('views/vehicle/MaintenanceExpenses/OtherMaintenances'))
);
const VehicleList = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/VehicleList'))
);
const DriverList = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/DriverList'))
);
const VehicleExpenseCategory = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/VehicleExpenseCategory'))
);
const VehicleExpenseHead = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/VehicleExpenseHead'))
);
const ServiceProvider = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/ServiceProvider'))
);
const FuelPump = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/FuelPump'))
);
const FuelPrice = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/FuelPrice'))
);
const ServiceItem = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/ServiceItem'))
);
const PaymentSource = Loadable(
  lazy(() => import('views/vehicle/VehicleLibrary/PaymentSource'))
);

// vehicle report
const VehicleExpenseSummary = Loadable(
  lazy(() => import('views/vehicle/Report/VehicleExpenseSummary'))
);
const ExpenseHeadSummary = Loadable(
  lazy(() => import('views/vehicle/Report/ExpenseHeadSummary'))
);
const FuelAnalysis = Loadable(
  lazy(() => import('views/vehicle/Report/FuelAnalysis'))
);
const MaintenanceSummary = Loadable(
  lazy(() => import('views/vehicle/Report/MaintenanceSummary'))
);
// end vehicle

// utilities routing
// setting routing
const ManageUser = Loadable(lazy(() => import('views/setting/ManageUser')));
const SingleUser = Loadable(
  lazy(() => import('views/setting/ManageUser/SingleUser'))
);
const UserInfo = Loadable(
  lazy(() => import('views/setting/ManageUser/SingleUser/UserInfo'))
);
const UserPermission = Loadable(
  lazy(() => import('views/setting/ManageUser/SingleUser/UserPermission'))
);

const Profile = Loadable(lazy(() => import('views/setting/Profile')));

// support routing
const Support = Loadable(lazy(() => import('views/support')));

// Support error routing
const Error404 = Loadable(lazy(() => import('views/Error404')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <PrivateRoute />,
  children: [
    {
      path: '/',
      element: <MainLayout />,
      children: [
        {
          path: '/',
          element: <Navigate to="dashboard" replace={true} />,
        },
        {
          path: 'dashboard',
          element: <Navigate to="default" replace={true} />,
        },
        {
          path: 'dashboard/default',
          element: <DashboardDefault />,
        },
        {
          path: 'pages',
          children: [
            {
              path: 'invoices',
              element: (
                <AuthenticationRoutes
                  allowedRoles={['super_admin', 'admin']}
                  allowedCodes={['invoices']}
                >
                  <Invoices />
                </AuthenticationRoutes>
              ),
            },
            {
              path: 'payment-receipts',
              element: (
                <AuthenticationRoutes
                  allowedRoles={['super_admin', 'admin']}
                  allowedCodes={['payment-receipts']}
                >
                  <PaymentReceipts />
                </AuthenticationRoutes>
              ),
            },
            {
              path: 'expenses',
              element: (
                <AuthenticationRoutes
                  allowedRoles={['super_admin', 'admin']}
                  allowedCodes={['expenses']}
                >
                  <Expenses />
                </AuthenticationRoutes>
              ),
            },
            {
              path: 'fixed-assets',
              element: (
                <AuthenticationRoutes
                  allowedRoles={['super_admin', 'admin']}
                  allowedCodes={['fixed-assets']}
                >
                  <FixedAssets />
                </AuthenticationRoutes>
              ),
            },
            {
              path: 'investments',
              element: (
                <AuthenticationRoutes
                  allowedRoles={['super_admin', 'admin']}
                  allowedCodes={['investments']}
                >
                  <Investment />
                </AuthenticationRoutes>
              ),
            },
            {
              path: 'library',
              children: [
                {
                  path: 'client-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['client-list']}
                    >
                      <ClientList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'product-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['product-list']}
                    >
                      <ProductList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'vendor-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['vendor-list']}
                    >
                      <VendorList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'expense-heads',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['expense-heads']}
                    >
                      <ExpenseHeads />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'expense-sub-heads',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['expense-sub-heads']}
                    >
                      <ExpenseSubHeads />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'asset-titles',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['asset-titles']}
                    >
                      <AssetTitles />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
            {
              path: 'report',
              children: [
                {
                  path: 'summary',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['summary']}
                    >
                      <ReportSummary />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'daily-report',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['daily-report']}
                    >
                      <DailyReport />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'due-report',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['due-report']}
                    >
                      <DueReport />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'advance-report',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['advance-report']}
                    >
                      <AdvanceReport />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'expense-summary',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['expense-summary']}
                    >
                      <ExpenseSummary />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: 'vehicle',
          children: [
            {
              path: 'operation-expenses',
              children: [
                {
                  path: 'fuel-expenses',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['fuel-expenses']}
                    >
                      <FuelExpenses />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'driver-salaries',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['driver-salaries']}
                    >
                      <DriverSalaries />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'other-expenses',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['other-expenses']}
                    >
                      <OtherExpenses />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
            {
              path: 'maintenance-expenses',
              children: [
                {
                  path: 'routine-services',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['routine-services']}
                    >
                      <RoutineServices />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'master-services',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['master-services']}
                    >
                      <MasterServices />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'other-maintenances',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['other-maintenances']}
                    >
                      <OtherMaintenances />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
            {
              path: 'vehicle-library',
              children: [
                {
                  path: 'vehicle-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['vehicle-list']}
                    >
                      <VehicleList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'product-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['product-list']}
                    >
                      <ProductList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'driver-list',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['driver-list']}
                    >
                      <DriverList />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'vehicle-expense-category',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['vehicle-expense-category']}
                    >
                      <VehicleExpenseCategory />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'vehicle-expense-head',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['vehicle-expense-head']}
                    >
                      <VehicleExpenseHead />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'service-provider',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['service-provider']}
                    >
                      <ServiceProvider />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'fuel-pump',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['fuel-pump']}
                    >
                      <FuelPump />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'fuel-price',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['fuel-price']}
                    >
                      <FuelPrice />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'service-items',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['service-items']}
                    >
                      <ServiceItem />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'payment-sources',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['payment-sources']}
                    >
                      <PaymentSource />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
            {
              path: 'vehicle-report',
              children: [
                {
                  path: 'summary',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['summary']}
                    >
                      <VehicleExpenseSummary />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'expense-head-summary',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['expense-head-summary']}
                    >
                      <ExpenseHeadSummary />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'fuel-analysis',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['fuel-analysis']}
                    >
                      <FuelAnalysis />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'maintenance-summary',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin']}
                      allowedCodes={['maintenance-summary']}
                    >
                      <MaintenanceSummary />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
          ],
        },

        {
          path: 'utils',
          children: [
            {
              path: 'setting',
              children: [
                {
                  path: 'manage-user',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin']}
                      allowedCodes={['manage-user']}
                    >
                      <ManageUser />
                    </AuthenticationRoutes>
                  ),
                },
                {
                  path: 'manage-user/:id',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin']}
                      allowedCodes={['manage-user']}
                    >
                      <SingleUser />
                    </AuthenticationRoutes>
                  ),
                  children: [
                    {
                      path: '',
                      element: <UserInfo />,
                    },
                    {
                      path: 'permission',
                      element: <UserPermission />,
                    },
                  ],
                },
                {
                  path: 'profile',
                  element: (
                    <AuthenticationRoutes
                      allowedRoles={['super_admin', 'admin', 'user']}
                      allowedCodes={['profile']}
                    >
                      <Profile />
                    </AuthenticationRoutes>
                  ),
                },
              ],
            },
            {
              path: 'support',
              element: <Support />,
            },
          ],
        },
        {
          path: '/*',
          element: <Error404 />,
        },
      ],
    },
  ],
};

export default MainRoutes;
